import { billKinds, kindMissing } from './bill-kinds';
import { ConsoleState, findDraft, groupRoutes, liveRevision } from './store';
import { RouteDef } from './types';

export interface EndpointReadiness {
  routeId: string;
  name: string;
  ready: boolean;
  /** What it still needs, in words, when it is not ready. */
  missing?: string;
  /** A live endpoint keeps serving, but the docs have moved on: what to configure before its next publish. */
  attention?: string;
}

export interface GroupReadiness {
  ready: boolean;
  endpoints: EndpointReadiness[];
}

/** The versions a shop would be served, narrowest first, as the data plane looks them up. */
const scopesFor = (state: ConsoleState, businessId?: string): string[] => {
  const business = businessId ? state.businesses?.find((item) => item.id === businessId) : undefined;
  return business ? [`business:${business.id}`, `tenant:${business.tenantId}`, 'shared'] : ['shared'];
};

/**
 * Whether one endpoint is live for a shop (or, with no shop, for the vendor as a whole). A live
 * version passed its tests to be published, so live is ready; one Casa's backend answers is live
 * as soon as it names its service.
 */
export const endpointReadiness = (state: ConsoleState, route: RouteDef, businessId?: string): EndpointReadiness => {
  const base = { routeId: route.id, name: route.name };
  if (route.signInInAddress)
    return { ...base, ready: false, missing: 'its key is in the address; a developer has to decide how it signs in' };
  if (route.upstream) return { ...base, ready: true };
  const scopes = scopesFor(state, businessId);
  const live = scopes.find((scope) => liveRevision(state, route.id, scope));
  if (live) {
    const kinds = billKinds(state, route.id, live);
    const attention = kinds.kinds.length
      ? kinds.split
        ? kinds.kinds.map(kindMissing).find(Boolean)
        : `its ${kinds.kinds.length} kinds of bill are not split by ${kinds.field} yet`
      : undefined;
    return { ...base, ready: true, ...(attention ? { attention } : {}) };
  }
  const drafted = scopes.find((scope) => findDraft(state, route.id, scope));
  const kinds = drafted ? billKinds(state, route.id, drafted) : undefined;
  const kind = kinds?.kinds.map(kindMissing).find(Boolean);
  return {
    ...base,
    ready: false,
    missing: !drafted
      ? 'nothing is mapped yet'
      : kinds?.kinds.length && !kinds.split
        ? `its ${kinds.kinds.length} kinds of bill are not split by ${kinds.field} yet`
        : (kind ?? 'not published yet'),
  };
};

export const groupReadiness = (state: ConsoleState, groupId: string, businessId?: string): GroupReadiness => {
  const endpoints = groupRoutes(state, groupId).map((route) => endpointReadiness(state, route, businessId));
  return { ready: endpoints.every((endpoint) => endpoint.ready), endpoints };
};

/**
 * The endpoints a shop may be sent traffic for, less those of a strict group that is not ready
 * for it yet. This is what the data plane's door is given; the console still shows the group as
 * the shop's, with what it is waiting for.
 */
export const liveForShop = (
  state: ConsoleState,
  routeIds: string[],
  businessId: string,
): { live: string[]; waiting: string[] } => {
  const waiting = new Set(
    state.groups
      .filter((group) => group.strict && !groupReadiness(state, group.id, businessId).ready)
      .flatMap((group) => groupRoutes(state, group.id).map((route) => route.id)),
  );
  return { live: routeIds.filter((id) => !waiting.has(id)), waiting: routeIds.filter((id) => waiting.has(id)) };
};
